/**
 * 시간 관련 유틸리티 함수들
 * 한국 시간(KST) 기준으로 처리
 */

// 한국 시간 기준 요일 가져오기 (0=일요일, 1=월요일, ..., 6=토요일)
export function getKoreanDay(): number {
  const now = new Date();
  const kstOffset = 9 * 60; // 한국은 UTC+9
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const kstTime = new Date(utc + (kstOffset * 60000));
  return kstTime.getDay();
}

// 한국 시간 기준 현재 시간 가져오기 (HH:mm 형식)
export function getKoreanTime(): string {
  const now = new Date();
  const kstOffset = 9 * 60; // 한국은 UTC+9
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const kstTime = new Date(utc + (kstOffset * 60000));
  
  const hours = kstTime.getHours().toString().padStart(2, '0');
  const minutes = kstTime.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

// 한국 시간 기준 내일 날짜 가져오기
export function getKoreanTomorrow(): Date {
  const now = new Date();
  const kstOffset = 9 * 60; // 한국은 UTC+9
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const kstTime = new Date(utc + (kstOffset * 60000));
  kstTime.setDate(kstTime.getDate() + 1);
  return kstTime;
}

// 30분 단위 시간 슬롯 생성
export function generateTimeSlots(
  startTime: string, 
  endTime: string, 
  breakStartTime?: string, 
  breakEndTime?: string
): string[] {
  const slots: string[] = [];
  
  // 시간을 분으로 변환하는 함수
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  // 분을 시간으로 변환하는 함수
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };
  
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const breakStartMinutes = breakStartTime ? timeToMinutes(breakStartTime) : null;
  const breakEndMinutes = breakEndTime ? timeToMinutes(breakEndTime) : null;
  
  // 30분 간격으로 시간 슬롯 생성
  for (let minutes = startMinutes; minutes <= endMinutes; minutes += 30) {
    // 브레이크타임 체크
    if (breakStartMinutes && breakEndMinutes) {
      if (minutes >= breakStartMinutes && minutes < breakEndMinutes) {
        continue; // 브레이크타임은 건너뛰기
      }
    }
    
    slots.push(minutesToTime(minutes));
  }
  
  return slots;
}

// 브레이크타임 체크
export function isTimeInBreak(
  time: string, 
  breakStartTime?: string, 
  breakEndTime?: string
): boolean {
  if (!breakStartTime || !breakEndTime) return false;
  
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  const timeMinutes = timeToMinutes(time);
  const breakStartMinutes = timeToMinutes(breakStartTime);
  const breakEndMinutes = timeToMinutes(breakEndTime);
  
  return timeMinutes >= breakStartMinutes && timeMinutes < breakEndMinutes;
}

// 시간을 "오후 2시 30분" 형식으로 변환
export function formatTimeSlot(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours < 12 ? '오전' : '오후';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${period} ${displayHours}시 ${minutes.toString().padStart(2, '0')}분`;
}

// 주문 가능 여부 확인
export function isOrderAvailable(
  orderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER',
  currentTime: string,
  operatingHours: {
    isOpen: boolean;
    openTime?: string;
    closeTime?: string;
    breakStartTime?: string;
    breakEndTime?: string;
    deadlineDiscountStartTime?: string;
    deadlineDiscountEndTime?: string;
    isClosed?: boolean;
  }
): boolean {
  // 휴무일이면 주문 불가
  if (operatingHours.isClosed) return false;
  
  // 영업 중이 아니면 주문 불가
  if (!operatingHours.isOpen) return false;
  
  // 브레이크타임 체크
  if (isTimeInBreak(currentTime, operatingHours.breakStartTime, operatingHours.breakEndTime)) {
    return false;
  }
  
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  const currentMinutes = timeToMinutes(currentTime);
  
  switch (orderType) {
    case 'DEADLINE_DISCOUNT':
      // 마감할인은 지정된 시간 내에만 주문 가능
      if (operatingHours.deadlineDiscountStartTime && operatingHours.deadlineDiscountEndTime) {
        const startMinutes = timeToMinutes(operatingHours.deadlineDiscountStartTime);
        const endMinutes = timeToMinutes(operatingHours.deadlineDiscountEndTime);
        return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
      }
      return false;
      
    case 'RESERVATION_DISCOUNT':
      // 예약할인은 영업종료 1시간 전까지 주문 가능
      if (operatingHours.closeTime) {
        const closeMinutes = timeToMinutes(operatingHours.closeTime);
        const deadlineMinutes = closeMinutes - 60; // 1시간 전
        return currentMinutes < deadlineMinutes;
      }
      return true;
      
    case 'REGULAR_ORDER':
      // 일반주문은 영업시간 내에 항상 가능
      if (operatingHours.openTime && operatingHours.closeTime) {
        const openMinutes = timeToMinutes(operatingHours.openTime);
        const closeMinutes = timeToMinutes(operatingHours.closeTime);
        return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
      }
      return true;
      
    default:
      return false;
  }
}

// 요일 이름 가져오기
export function getDayName(dayIndex: number): string {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayNames[dayIndex];
}

// 한국어 요일 이름 가져오기
export function getKoreanDayName(dayIndex: number): string {
  const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
  return dayNames[dayIndex];
}